import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Copy, Users } from "lucide-react";

export default function TeacherDashboard() {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", subject: "math", grade_level: "3" });
  const [selected, setSelected] = useState(null);
  const [roster, setRoster] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/teacher/classes");
    setClasses(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/teacher/classes", form);
      setCreating(false); setForm({ name: "", subject: "math", grade_level: "3" });
      load(); toast.success(t("teacher.create") + " ✓");
    } catch (err) {
      console.warn("Class create failed:", err);
      toast.error(t("errors.generic"));
    }
  }
  async function openClass(c) {
    setSelected(c);
    const r = await api.get(`/teacher/class/${c.id}/students`);
    setRoster(r.data);
  }

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-adult font-bold text-3xl text-slate-900">{t("teacher.classesTitle")}</h1>
          <p className="text-slate-600">{t("teacher.classesBody")}</p>
        </div>
        <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => setCreating((c) => !c)} data-testid="teacher-new-class">{t("teacher.newClass")}</Button>
      </div>

      {creating && (
        <form onSubmit={create} className="hb-card p-5 mt-4 grid sm:grid-cols-4 gap-3 items-end">
          <div><Label>{t("teacher.name")}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="class-name" /></div>
          <div><Label>{t("teacher.subject")}</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="class-subject" /></div>
          <div><Label>{t("teacher.grade")}</Label><Input required value={form.grade_level} onChange={(e) => setForm({ ...form, grade_level: e.target.value })} data-testid="class-grade" /></div>
          <Button className="rounded-full bg-slate-900 text-white" data-testid="class-create">{t("teacher.create")}</Button>
        </form>
      )}

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        {classes.map((c) => (
          <div key={c.id} className="hb-card p-5" data-testid={`class-${c.id}`}>
            <div className="font-adult font-bold text-slate-900">{c.name}</div>
            <div className="text-xs text-slate-500">{c.subject} · {t("student.grade")} {c.grade_level}</div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs">
                <div className="uppercase tracking-widest text-slate-500 font-semibold">{t("teacher.classCode")}</div>
                <div className="font-mono font-bold text-slate-900">{c.class_code}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(c.class_code); toast.success(t("common.copied")); }}><Copy className="w-4 h-4"/></Button>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => openClass(c)} data-testid={`class-open-${c.id}`}>{t("teacher.roster")}</Button>
              <Link to="/app/teacher/assignments"><Button size="sm" variant="outline" className="rounded-full">{t("teacher.assignmentsTab")}</Button></Link>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-adult font-bold text-2xl text-slate-900">{selected.name} · {t("teacher.roster")}</div>
              <button onClick={() => setSelected(null)} aria-label={t("common.close")}>×</button>
            </div>
            <div className="mt-3 max-h-[60vh] overflow-auto">
              {roster.length === 0 && <div className="text-sm text-slate-600">{t("teacher.noStudents", { code: selected.class_code })}</div>}
              {roster.map((r) => (
                <div key={r.student.id} className="p-3 rounded-xl border border-slate-200 mb-2 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{r.student.profile?.display_name || r.student.full_name}</div>
                    <div className="text-xs text-slate-500">{t("student.grade")} {r.student.profile?.grade_level || "—"}</div>
                  </div>
                  <div className="text-xs text-slate-600 flex items-center gap-1"><Users className="w-3.5 h-3.5"/> {Math.round(r.confidence.reduce((s, x) => s + x.confidence, 0) / r.confidence.length)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
